import { useEffect, useState } from "react";
import { ArrowUpRight, Instagram, Linkedin, Phone, Mail, MapPin, Youtube, Play } from "lucide-react";
import { CONTACT, INSTA_FEED, IMAGES, API } from "@/data/site";
import { useLead } from "@/App";
import { FadeUp } from "./motion";

function InstaGrid() {
  const [live, setLive] = useState(null);
  useEffect(() => {
    fetch(`${API}/instagram/media`)
      .then((r) => r.json())
      .then((d) => setLive(d.configured && d.data.length ? d.data.slice(0, 6) : []))
      .catch(() => setLive([]));
  }, []);

  const items = live && live.length
    ? live.map((p) => ({ img: p.image, href: p.permalink, reel: p.type === "REELS", caption: p.caption }))
    : INSTA_FEED.map((f) => ({ ...f, reel: false }));

  return (
    <div className="mt-5 grid grid-cols-3 gap-2" data-testid="instagram-feed">
      {items.map((f, i) => (
        <a key={i} href={f.href} target="_blank" rel="noreferrer" data-testid={`footer-insta-${i}`} className="group relative block aspect-square overflow-hidden rounded-md">
          <img src={f.img} alt={f.caption || "Instagram post by Bengaluru Interiors"} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
          <span className="absolute inset-0 flex items-center justify-center bg-[#1B1B1B]/50 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            <Instagram size={18} className="text-[#F7F5F2]" />
          </span>
          {f.reel && (
            <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#1B1B1B]/70">
              <Play size={9} className="fill-[#F7F5F2] text-[#F7F5F2]" />
            </span>
          )}
        </a>
      ))}
    </div>
  );
}

export default function Footer() {
  const { openConsult } = useLead();

  const go = (e, href) => {
    e.preventDefault();
    window.__lenis?.scrollTo(href, { offset: -70, duration: 1.4 });
  };

  return (
    <footer data-testid="footer" className="grain relative bg-[#1B1B1B] pt-24 md:pt-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <FadeUp className="border-b border-[#F7F5F2]/10 pb-16">
          <p className="font-num text-[11px] uppercase tracking-[0.4em] text-[#B68D40]">Ready when you are</p>
          <div className="mt-6 flex flex-wrap items-end justify-between gap-8">
            <h2 className="max-w-2xl font-display text-5xl leading-[1.05] tracking-tight text-[#F7F5F2] md:text-7xl">
              Let's craft <span className="italic text-[#B68D40]">your</span> home.
            </h2>
            <button
              onClick={() => openConsult("footer")}
              data-testid="footer-cta-btn"
              className="group flex items-center gap-3 rounded-full bg-[#B68D40] px-8 py-4 text-sm font-semibold uppercase tracking-widest text-[#1B1B1B] transition-colors duration-300 hover:bg-[#F7F5F2]"
            >
              Book Free Consultation
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </button>
          </div>
        </FadeUp>

        <div className="grid grid-cols-1 gap-12 py-16 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <span className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-full bg-white shadow-[0_8px_24px_rgba(0,0,0,0.35)]">
              <img src={IMAGES.logo} alt="Bengaluru Interior Design Company logo" className="h-11 w-11 object-contain" />
            </span>
            <p className="mt-4 font-display text-xl text-[#F7F5F2]">
              Bengaluru <span className="italic text-[#B68D40]">Interiors</span>
            </p>
            <p className="mt-1 font-num text-[9px] uppercase tracking-[0.35em] text-[#F7F5F2]/50">By Sohan Raju</p>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-[#F7F5F2]/55">
              Luxury crafted around your lifestyle. Where beautiful homes begin.
            </p>
            <div className="mt-6 flex gap-3">
              <a href={CONTACT.instagram} target="_blank" rel="noreferrer" data-testid="footer-instagram" className="flex h-10 w-10 items-center justify-center rounded-full border border-[#F7F5F2]/20 text-[#F7F5F2]/80 transition-colors hover:border-[#B68D40] hover:text-[#B68D40]" aria-label="Instagram">
                <Instagram size={16} />
              </a>
              <a href={CONTACT.youtube} target="_blank" rel="noreferrer" data-testid="footer-youtube" className="flex h-10 w-10 items-center justify-center rounded-full border border-[#F7F5F2]/20 text-[#F7F5F2]/80 transition-colors hover:border-[#B68D40] hover:text-[#B68D40]" aria-label="YouTube">
                <Youtube size={16} />
              </a>
              <a href={CONTACT.linkedin} target="_blank" rel="noreferrer" data-testid="footer-linkedin" className="flex h-10 w-10 items-center justify-center rounded-full border border-[#F7F5F2]/20 text-[#F7F5F2]/80 transition-colors hover:border-[#B68D40] hover:text-[#B68D40]" aria-label="LinkedIn">
                <Linkedin size={16} />
              </a>
            </div>
          </div>

          <div>
            <p className="font-num text-[10px] uppercase tracking-[0.3em] text-[#B68D40]">Explore</p>
            <ul className="mt-5 space-y-3">
              {[["Projects", "#projects"], ["Services", "#services"], ["Process", "#process"], ["Testimonials", "#testimonials"], ["About", "#about"], ["Contact", "#contact"]].map(([label, href]) => (
                <li key={href}>
                  <a href={href} onClick={(e) => go(e, href)} data-testid={`footer-link-${label.toLowerCase()}`} className="text-sm text-[#F7F5F2]/60 transition-colors hover:text-[#B68D40]">
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-num text-[10px] uppercase tracking-[0.3em] text-[#B68D40]">Reach Us</p>
            <ul className="mt-5 space-y-4 text-sm text-[#F7F5F2]/60">
              <li className="flex items-start gap-3"><Phone size={15} className="mt-0.5 shrink-0 text-[#B68D40]" /> {CONTACT.phone} · {CONTACT.phone2}</li>
              <li className="flex items-start gap-3"><Mail size={15} className="mt-0.5 shrink-0 text-[#B68D40]" /> {CONTACT.email}</li>
              <li className="flex items-start gap-3"><MapPin size={15} className="mt-0.5 shrink-0 text-[#B68D40]" /> {CONTACT.address}</li>
            </ul>
          </div>

          <div>
            <p className="font-num text-[10px] uppercase tracking-[0.3em] text-[#B68D40]">On Instagram</p>
            <p className="mt-2 text-xs text-[#F7F5F2]/45">@bengaluru_interiors_by_sohan</p>
            <InstaGrid />
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-3 border-t border-[#F7F5F2]/10 py-8 md:flex-row">
          <p className="text-xs text-[#F7F5F2]/40">© 2026 Bengaluru Interiors By Sohan Raju. All rights reserved.</p>
          <p className="font-num text-[10px] uppercase tracking-[0.3em] text-[#F7F5F2]/30">Designed for Living · Built to Inspire</p>
        </div>
      </div>
    </footer>
  );
}
